import React, { useState } from "react";
import { Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import UserDashboardLayout from "../components/user/UserDashboardLayout";
import SEO from "../components/SEO";
import { UserDashboardService } from "../services/user/UserDashboardService";
import { useNotification } from "../context/NotificationContext";

export default function ChangePassword() {
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const [loading, setLoading] = useState(false);
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false,
    });

    const [formData, setFormData] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });

    const [errors, setErrors] = useState({});

    const togglePasswordVisibility = (field) => {
        setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.currentPassword) newErrors.currentPassword = "Current password is required.";
        if (!formData.newPassword) newErrors.newPassword = "New password is required.";
        else if (formData.newPassword.length < 8) newErrors.newPassword = "Password must be at least 8 characters.";

        if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = "Passwords do not match.";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            const response = await UserDashboardService.changePassword({
                currentPassword: formData.currentPassword,
                newPassword: formData.newPassword
            });

            if (response.status === "success") {
                showNotification({
                    type: "success",
                    title: "Success",
                    message: "Your password has been changed successfully."
                });
                setFormData({
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: "",
                });
                // Redirect back to dashboard after a short delay to allow notification to be seen
                setTimeout(() => {
                    navigate("/dashboard");
                }, 1500);
            } else {
                showNotification({
                    type: "failed",
                    title: "Failed",
                    message: response.message || "Failed to change password."
                });
            }
        } catch (error) {
            showNotification({
                type: "failed",
                title: "Error",
                message: error.response?.data?.message || "An unexpected error occurred."
            });
        } finally {
            setLoading(false);
        }
    };

    const inputClasses = "w-full bg-[#fafafa] border border-black/5 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-jazz-brown/30 transition-colors pr-12 text-black font-semibold";
    const labelClasses = "block text-[10px] font-bold tracking-[2px] uppercase text-black/80 mb-2";

    return (
        <UserDashboardLayout title="Security Settings" subtitle="Keep your account safe by updating your password regularly.">
            <SEO title="Security Settings" url="/dashboard/change-password" />
            <section className="bg-white rounded-2xl shadow-sm border border-black/5 p-8 max-w-2xl">
                <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 bg-jazz-brown/5 rounded-xl flex items-center justify-center text-jazz-brown">
                        <Lock size={20} />
                    </div>
                    <div>
                        <h2 className="text-xl font-serif italic text-black">Change Password</h2>
                        <p className="text-[10px] tracking-[1px] uppercase text-black/60">Update your account credentials</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Current Password */}
                    <div className="relative">
                        <label className={labelClasses}>Current Password</label>
                        <div className="relative">
                            <input
                                type={showPasswords.current ? "text" : "password"}
                                className={`${inputClasses} ${errors.currentPassword ? "border-red-500" : ""}`}
                                value={formData.currentPassword}
                                onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                                placeholder="Enter current password"
                            />
                            <button
                                type="button"
                                onClick={() => togglePasswordVisibility("current")}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-black/40 hover:text-black transition-colors"
                            >
                                {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        {errors.currentPassword && <p className="text-[10px] text-red-500 mt-1 font-bold italic">{errors.currentPassword}</p>}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 border-t border-black/5">
                        {/* New Password */}
                        <div className="relative">
                            <label className={labelClasses}>New Password</label>
                            <div className="relative">
                                <input
                                    type={showPasswords.new ? "text" : "password"}
                                    className={`${inputClasses} ${errors.newPassword ? "border-red-500" : ""}`}
                                    value={formData.newPassword}
                                    onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                                    placeholder="Minimum 8 characters"
                                />
                                <button
                                    type="button"
                                    onClick={() => togglePasswordVisibility("new")}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-black/40 hover:text-black transition-colors"
                                >
                                    {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                            {errors.newPassword && <p className="text-[10px] text-red-500 mt-1 font-bold italic">{errors.newPassword}</p>}
                        </div>

                        {/* Confirm New Password */}
                        <div className="relative">
                            <label className={labelClasses}>Confirm New Password</label>
                            <div className="relative">
                                <input
                                    type={showPasswords.confirm ? "text" : "password"}
                                    className={`${inputClasses} ${errors.confirmPassword ? "border-red-500" : ""}`}
                                    value={formData.confirmPassword}
                                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                    placeholder="Repeat new password"
                                />
                                <button
                                    type="button"
                                    onClick={() => togglePasswordVisibility("confirm")}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-black/40 hover:text-black transition-colors"
                                >
                                    {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                            {errors.confirmPassword && <p className="text-[10px] text-red-500 mt-1 font-bold italic">{errors.confirmPassword}</p>}
                        </div>
                    </div>

                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full md:w-auto px-8 py-4 bg-black text-white rounded-xl font-bold text-[10px] tracking-[2px] uppercase hover:bg-jazz-brown transition-all disabled:bg-black/20 flex items-center justify-center gap-3 group"
                        >
                            {loading ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <Lock size={16} className="group-hover:rotate-12 transition-transform" />
                            )}
                            {loading ? "Updating Password..." : "Update Password"}
                        </button>
                    </div>
                </form>
            </section>
        </UserDashboardLayout>
    );
}
